import React, { useState, useEffect } from "react";
import { Card, Row, Col, Form, Button, Alert } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Formik } from 'formik';
import * as Yup from 'yup';
import PageTitle from "../../layouts/PageTitle";
import CompanySearchSelect from "../../components/SearchSelects/CompanySearchSelect";
import { 
  createDepositorAction, 
  updateDepositorAction, 
  getDepositorByIdAction,
  clearCurrentDepositor,
  clearErrors
} from "../../../store/actions/DepositorsActions";
import DepositorsService from "../../../services/DepositorsService";
import swal from "sweetalert";

const AddUpdateDepositor = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const { 
    data: currentDepositor, 
    loading, 
    error, 
    success,
    validationErrors 
  } = useSelector((state) => state.depositors);

  const [initialValues, setInitialValues] = useState({
    name: '',
    address: '',
    city: '',
    district: '',
    state: '',
    phoneNo: '',
    email: '',
    companyId: '',
    isPartner: false,
    status: 'Pending'
  });

  useEffect(() => {
    if (isEdit) {
      dispatch(getDepositorByIdAction(id));
    }

    return () => {
      dispatch(clearCurrentDepositor());
      dispatch(clearErrors());
    };
  }, [dispatch, id, isEdit]);

  useEffect(() => {
    if (isEdit && currentDepositor && Object.keys(currentDepositor).length > 0) {
      setInitialValues({
        name: currentDepositor.name || '',
        address: currentDepositor.address || '',
        city: currentDepositor.city || '',
        district: currentDepositor.district || '',
        state: currentDepositor.state || '',
        phoneNo: currentDepositor.phoneNo || currentDepositor.phoneNumber || '',
        email: currentDepositor.email || '',
        companyId: currentDepositor.companyId || '',
        isPartner: currentDepositor.isPartner || false,
        status: currentDepositor.status || 'Pending'
      });
    }
  }, [currentDepositor, isEdit]);

  // Validation schema
  const validationSchema = Yup.object({
    name: Yup.string()
      .required("Name is required")
      .min(2, "Name must be at least 2 characters")
      .max(100, "Name cannot exceed 100 characters")
      .matches(/^[a-zA-Z\s.-]+$/, "Name can only contain letters, spaces, dots, and hyphens"),
    address: Yup.string()
      .max(255, "Address cannot exceed 255 characters"),
    city: Yup.string()
      .max(100, "City cannot exceed 100 characters"),
    district: Yup.string()
      .max(100, "District cannot exceed 100 characters"),
    state: Yup.string()
      .max(100, "State cannot exceed 100 characters"),
    phoneNo: Yup.string()
      .matches(/^[\d+\-() ]{10,15}$/, "Phone number must be 10-15 digits and can include +, -, (, ), spaces"),
    email: Yup.string()
      .email("Invalid email format"),
    companyId: Yup.string()
      .required("Company is required"),
    isPartner: Yup.boolean(),
    status: Yup.string()
      .oneOf(['Active', 'Inactive', 'Pending', 'Rejected'], "Status must be one of: Active, Inactive, Pending, Rejected")
      .required("Status is required")
  });

  // Helper function to get field validation error
  const getFieldValidationError = (fieldName) => {
    if (!validationErrors || !Array.isArray(validationErrors)) return null;
    const fieldError = validationErrors.find(err => err.path === fieldName);
    return fieldError ? fieldError.msg : null;
  };

  // Handle form submission
  const handleSubmit = (values, { setSubmitting }) => {
    const submitData = {
      name: values.name?.trim(),
      address: values.address?.trim() || null,
      city: values.city?.trim() || null,
      district: values.district?.trim() || null,
      state: values.state?.trim() || null,
      phoneNo: values.phoneNo?.trim() || null,
      email: values.email?.trim() || null,
      companyId: values.companyId,
      isPartner: values.isPartner,
      status: values.status
    };

    // Remove null/empty values for cleaner API call
    Object.keys(submitData).forEach(key => {
      if (submitData[key] === null || submitData[key] === '') {
        delete submitData[key];
      }
    });

    if (isEdit) {
      dispatch(updateDepositorAction(id, submitData, navigate));
    } else {
      dispatch(createDepositorAction(submitData, navigate));
    }
    
    setSubmitting(false);
  };

  return (
    <>
      <PageTitle 
        activeMenu={isEdit ? "Edit Depositor" : "Add Depositor"} 
        motherMenu="Finance" 
      />
      
      <div className="row">
        <div className="col-lg-12">
          <div className="card">
            <div className="card-header">
              <h4 className="card-title">{isEdit ? 'Edit' : 'Add'} Depositor</h4>
            </div>
            <div className="card-body">
              {/* Error Display */}
              {error && (
                <div className="alert alert-danger">
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => dispatch(clearErrors())}
                  ></button>
                  <strong>Error!</strong> {typeof error === 'string' ? error : error.message || 'An error occurred'}
                </div>
              )}

              {/* Validation Errors Display */}
              {validationErrors && validationErrors.length > 0 && (
                <div className="alert alert-danger">
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => dispatch(clearErrors())}
                  ></button>
                  <strong>Validation Errors:</strong>
                  <ul className="mb-0 mt-2">
                    {validationErrors.map((err, index) => (
                      <li key={index}>{err.msg}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Loading Spinner */}
              {loading && isEdit ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="sr-only">Loading...</span>
                  </div>
                  <p className="mt-3">Loading depositor data...</p>
                </div>
              ) : (
                <Formik
                  initialValues={initialValues}
                  validationSchema={validationSchema}
                  enableReinitialize={true}
                  onSubmit={handleSubmit}
                >
                  {({
                    values,
                    errors,
                    touched,
                    handleChange,
                    handleBlur,
                    handleSubmit,
                    isSubmitting,
                    resetForm
                  }) => (
                    <form onSubmit={handleSubmit}>
                      {/* Basic Information Section */}
                      <div className="row">
                        <div className="col-12">
                          <h5 className="text-primary mb-3">Basic Information</h5>
                        </div>
                        
                        <div className="col-md-6 mb-3">
                          <label className="form-label">
                            Name <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            name="name"
                            className={`form-control ${
                              (touched.name && errors.name) || getFieldValidationError('name') ? 'is-invalid' : ''
                            }`}
                            value={values.name}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            placeholder="Enter depositor name"
                          />
                          {(touched.name && errors.name) && (
                            <div className="invalid-feedback">{errors.name}</div>
                          )}
                          {getFieldValidationError('name') && (
                            <div className="invalid-feedback d-block">{getFieldValidationError('name')}</div>
                          )}
                        </div>

                        <div className="col-md-6 mb-3">
                          <label className="form-label">Email</label>
                          <input
                            type="email"
                            name="email"
                            className={`form-control ${
                              (touched.email && errors.email) || getFieldValidationError('email') ? 'is-invalid' : ''
                            }`}
                            value={values.email}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            placeholder="Enter email address"
                          />
                          {(touched.email && errors.email) && (
                            <div className="invalid-feedback">{errors.email}</div>
                          )}
                          {getFieldValidationError('email') && (
                            <div className="invalid-feedback d-block">{getFieldValidationError('email')}</div>
                          )}
                        </div>

                        <div className="col-md-6 mb-3">
                          <label className="form-label">Phone Number</label>
                          <input
                            type="tel"
                            name="phoneNo"
                            className={`form-control ${
                              (touched.phoneNo && errors.phoneNo) || getFieldValidationError('phoneNo') ? 'is-invalid' : ''
                            }`}
                            value={values.phoneNo}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            placeholder="Enter phone number"
                          />
                          {(touched.phoneNo && errors.phoneNo) && (
                            <div className="invalid-feedback">{errors.phoneNo}</div>
                          )}
                          {getFieldValidationError('phoneNo') && (
                            <div className="invalid-feedback d-block">{getFieldValidationError('phoneNo')}</div>
                          )}
                        </div>

                        <div className="col-md-6 mb-3">
                          <CompanySearchSelect
                            value={values.companyId}
                            onChange={(selectedCompanyId) => {
                              // Create a synthetic event for Formik
                              const syntheticEvent = {
                                target: {
                                  name: 'companyId',
                                  value: selectedCompanyId || ''
                                }
                              };
                              handleChange(syntheticEvent);
                            }}
                            name="companyId"
                            label="Company"
                            required={true}
                            error={(touched.companyId && errors.companyId) || getFieldValidationError('companyId')}
                            placeholder="Search and select company..."
                            disabled={isSubmitting}
                            showLabel={true}
                            allowClear={true}
                          />
                        </div>

                        <div className="col-md-6 mb-3">
                          <label className="form-label">
                            Status <span className="text-danger">*</span>
                          </label>
                          <select
                            name="status"
                            className={`form-select form-control ${
                              (touched.status && errors.status) || getFieldValidationError('status') ? 'is-invalid' : ''
                            }`}
                            value={values.status}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          >
                            <option value="Pending">Pending</option>
                            <option value="Active">Active</option>
                            <option value="Inactive">Inactive</option>
                            <option value="Rejected">Rejected</option>
                          </select>
                          {(touched.status && errors.status) && (
                            <div className="invalid-feedback">{errors.status}</div>
                          )}
                          {getFieldValidationError('status') && (
                            <div className="invalid-feedback d-block">{getFieldValidationError('status')}</div>
                          )}
                        </div>

                        <div className="col-md-6 mb-3">
                          <div className="form-check mt-4">
                            <input
                              type="checkbox"
                              name="isPartner"
                              className="form-check-input"
                              checked={values.isPartner}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            <label className="form-check-label">
                              Is Partner
                            </label>
                          </div>
                        </div>
                      </div>

                      {/* Address Information Section */}
                      <div className="row">
                        <div className="col-12">
                          <h5 className="text-primary mb-3 mt-4">Address Information</h5>
                        </div>
                        
                        <div className="col-12 mb-3">
                          <label className="form-label">Address</label>
                          <textarea
                            name="address"
                            rows={3}
                            className={`form-control ${
                              (touched.address && errors.address) || getFieldValidationError('address') ? 'is-invalid' : ''
                            }`}
                            value={values.address}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            placeholder="Enter complete address"
                          />
                          {(touched.address && errors.address) && (
                            <div className="invalid-feedback">{errors.address}</div>
                          )}
                          {getFieldValidationError('address') && (
                            <div className="invalid-feedback d-block">{getFieldValidationError('address')}</div>
                          )}
                        </div>

                        <div className="col-md-4 mb-3">
                          <label className="form-label">City</label>
                          <input
                            type="text"
                            name="city"
                            className={`form-control ${
                              (touched.city && errors.city) || getFieldValidationError('city') ? 'is-invalid' : ''
                            }`}
                            value={values.city}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            placeholder="Enter city"
                          />
                          {(touched.city && errors.city) && (
                            <div className="invalid-feedback">{errors.city}</div>
                          )}
                          {getFieldValidationError('city') && (
                            <div className="invalid-feedback d-block">{getFieldValidationError('city')}</div>
                          )}
                        </div>

                        <div className="col-md-4 mb-3">
                          <label className="form-label">District</label>
                          <input
                            type="text"
                            name="district"
                            className={`form-control ${
                              (touched.district && errors.district) || getFieldValidationError('district') ? 'is-invalid' : ''
                            }`}
                            value={values.district}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            placeholder="Enter district"
                          />
                          {(touched.district && errors.district) && (
                            <div className="invalid-feedback">{errors.district}</div>
                          )}
                          {getFieldValidationError('district') && (
                            <div className="invalid-feedback d-block">{getFieldValidationError('district')}</div>
                          )}
                        </div>

                        <div className="col-md-4 mb-3">
                          <label className="form-label">State</label>
                          <input
                            type="text"
                            name="state"
                            className={`form-control ${
                              (touched.state && errors.state) || getFieldValidationError('state') ? 'is-invalid' : ''
                            }`}
                            value={values.state}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            placeholder="Enter state"
                          />
                          {(touched.state && errors.state) && (
                            <div className="invalid-feedback">{errors.state}</div>
                          )}
                          {getFieldValidationError('state') && (
                            <div className="invalid-feedback d-block">{getFieldValidationError('state')}</div>
                          )}
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="d-flex justify-content-end gap-2 mt-4">
                        <button
                          type="button"
                          className="btn btn-light"
                          onClick={() => navigate('/depositors')}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => resetForm()}
                          disabled={isSubmitting}
                        >
                          Reset
                        </button>
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                              {isEdit ? 'Updating...' : 'Creating...'}
                            </>
                          ) : (
                            isEdit ? 'Update Depositor' : 'Create Depositor'
                          )}
                        </button>
                      </div>
                    </form>
                  )}
                </Formik>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddUpdateDepositor;